<template>
  <div class="coverage-details-card h-full">
    <div class="coverage-container h-full overflow-y-auto">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-message mb-4 text-center">
          <div class="inline-flex items-center rounded-lg bg-orange-50 px-4 py-2">
            <icon-vue icon="mdi:loading" class="mr-2 animate-spin text-orange-600" />
            <span class="text-sm font-medium text-orange-700">{{ loadingMessage }}</span>
          </div>
        </div>

        <!-- 骨架屏动画 -->
        <div class="space-y-2">
          <div
            v-for="i in 3"
            :key="i"
            class="flex animate-pulse items-center justify-between border-b border-gray-100 py-3"
          >
            <div class="flex-1">
              <div class="mb-1 h-3 w-20 rounded bg-gray-300"></div>
              <div class="h-2 w-32 rounded bg-gray-200"></div>
            </div>
            <div class="text-right">
              <div class="mb-1 h-4 w-8 rounded bg-gray-300"></div>
              <div class="h-2 w-12 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="isError" class="error-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="mx-auto h-16 w-16 flex items-center justify-center border-2 border-gray-200 rounded-full bg-gray-100"
          >
            <icon-vue icon="mdi:chart-timeline-variant" class="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <h3 class="mb-2 text-base font-medium text-gray-700">覆盖率分析未完成</h3>
        <p class="text-sm leading-relaxed text-gray-500">
          需要重新分析才能生成覆盖率详情
          <br />
          <span class="text-xs text-gray-400">点击顶部"重新分析"按钮开始</span>
        </p>
      </div>

      <!-- 覆盖率详情 -->
      <div v-else-if="hasCoverageData" class="coverage-content">
        <!-- 总体覆盖率概览 -->
        <div v-if="overallCoverage" class="mb-4 border rounded-lg p-3" :class="getOverallCoverageClass()">
          <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-medium text-gray-800">总体预估覆盖率</span>
            <span class="text-lg font-bold" :class="getOverallCoverageTextClass()">
              {{ overallCoverage.percentage }}%
            </span>
          </div>
          <div class="h-2 w-full rounded-full bg-gray-200">
            <div
              class="h-2 rounded-full transition-all duration-1000"
              :class="getOverallCoverageBarClass()"
              :style="{ width: `${overallCoverage.percentage}%` }"
            ></div>
          </div>
        </div>

        <!-- 文件级覆盖率详情 -->
        <div class="space-y-2">
          <coverage-file-item
            v-for="(file, index) in fileDetails"
            :key="file.file_path"
            :file-detail="file"
            :coverage-gaps="getCoverageGaps(file.file_path)"
            :index="index"
            @view-detail="handleViewFileDetail"
          />
        </div>

        <!-- 覆盖缺口警告 -->
        <div v-if="hasHighRiskGaps" class="mt-4 border border-red-200 rounded-lg bg-red-50 p-3">
          <div class="mb-2 flex items-center">
            <warning-outlined class="mr-2 text-red-500" />
            <span class="text-sm font-medium text-red-800">发现 {{ highRiskGapsCount }} 个高风险覆盖缺口</span>
          </div>
          <a-button
            type="link"
            size="small"
            class="h-auto p-0 text-xs text-red-700 hover:text-red-900"
            @click="handleViewCoverageGaps"
          >
            查看详细风险分析 →
          </a-button>
        </div>
      </div>

      <!-- 等待分析状态 -->
      <div v-else-if="status === 'idle'" class="empty-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="relative mx-auto h-12 w-12 flex items-center justify-center rounded-full from-orange-500 to-red-500 bg-gradient-to-r shadow-lg"
          >
            <icon-vue icon="mdi:chart-timeline-variant" class="animate-pulse text-xl text-white" />
            <!-- 波纹效果 -->
            <div
              class="absolute inset-0 animate-ping border-2 border-orange-300 rounded-full opacity-60"
              style="animation-duration: 3s"
            ></div>
            <div
              class="absolute inset-0 animate-ping border-2 border-red-300 rounded-full opacity-40"
              style="animation-delay: 1s; animation-duration: 3s"
            ></div>
          </div>
        </div>
        <h3 class="mb-2 text-sm font-medium text-gray-700">智能覆盖率分析</h3>
        <p class="text-xs leading-relaxed text-gray-500">
          AI将计算测试覆盖范围，生成
          <br />
          <span class="font-medium text-orange-600">精准覆盖率报告</span>
          ，优化测试策略
        </p>
        <div class="mt-4 flex justify-center">
          <div class="flex items-center text-xs text-gray-400 space-x-2">
            <span class="h-2 w-2 animate-pulse rounded-full bg-orange-500"></span>
            <span>等待分析启动</span>
          </div>
        </div>
      </div>

      <!-- 无数据状态 -->
      <div v-else class="no-data-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="mx-auto h-12 w-12 flex items-center justify-center border-2 border-gray-200 rounded-full bg-gray-100"
          >
            <icon-vue icon="mdi:chart-timeline-variant" class="text-xl text-gray-400" />
          </div>
        </div>
        <h3 class="mb-2 text-sm font-medium text-gray-700">暂无覆盖率数据</h3>
        <p class="text-xs text-gray-500">可能是因为代码变更范围较小或缺少相关测试数据</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  AnalysisStatus,
  CoverageEstimation,
  CoverageGap,
  FileCoverageDetail,
} from '@/types/precision-new/ai-analysis';
import { Icon as IconVue } from '@iconify/vue';
import { computed } from 'vue';
import CoverageFileItem from './CoverageFileItem.vue';

// Props
interface Props {
  status: AnalysisStatus;
  coverageData?: CoverageEstimation;
  loading?: boolean;
  loadingMessage?: string;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  coverageData: undefined,
  loading: false,
  loadingMessage: '正在计算覆盖率...',
  error: '',
});

// Emits
const emit = defineEmits(['retry', 'viewFileDetail', 'viewCoverageGaps']);

// Computed
const isLoading = computed(() => props.loading || props.status === 'analyzing');
const isError = computed(() => props.status === 'error' || !!props.error);
const hasCoverageData = computed(
  () =>
    props.coverageData &&
    props.coverageData.file_coverage_details &&
    props.coverageData.file_coverage_details.length > 0,
);

const overallCoverage = computed(() => {
  if (!props.coverageData?.estimated_coverage) {
    return null;
  }
  return {
    percentage: Math.round(props.coverageData.estimated_coverage * 100),
  };
});

const fileDetails = computed(() => props.coverageData?.file_coverage_details || []);

const coverageGaps = computed(() => props.coverageData?.coverage_gaps || []);

const hasHighRiskGaps = computed(() => {
  return coverageGaps.value.some((gap) => gap.risk_level === 'high' || gap.risk_level === 'critical');
});

const highRiskGapsCount = computed(() => {
  return coverageGaps.value.filter((gap) => gap.risk_level === 'high' || gap.risk_level === 'critical').length;
});

// 方法
function getCoverageGaps(filePath: string): CoverageGap[] {
  return coverageGaps.value.filter((gap) => gap.file_path === filePath);
}

function getOverallCoverageClass() {
  if (!overallCoverage.value) {
    return 'bg-gray-50 border-gray-200';
  }
  const percentage = overallCoverage.value.percentage;
  if (percentage >= 80) {
    return 'bg-green-50 border-green-200';
  }
  if (percentage >= 60) {
    return 'bg-yellow-50 border-yellow-200';
  }
  return 'bg-red-50 border-red-200';
}

function getOverallCoverageTextClass() {
  if (!overallCoverage.value) {
    return 'text-gray-600';
  }
  const percentage = overallCoverage.value.percentage;
  if (percentage >= 80) {
    return 'text-green-600';
  }
  if (percentage >= 60) {
    return 'text-yellow-600';
  }
  return 'text-red-500';
}

function getOverallCoverageBarClass() {
  if (!overallCoverage.value) {
    return 'bg-gray-500';
  }
  const percentage = overallCoverage.value.percentage;
  if (percentage >= 80) {
    return 'bg-green-500';
  }
  if (percentage >= 60) {
    return 'bg-yellow-500';
  }
  return 'bg-red-500';
}

function handleViewFileDetail(fileDetail: FileCoverageDetail) {
  const gaps = getCoverageGaps(fileDetail.file_path);
  emit('viewFileDetail', fileDetail, gaps);
}

function handleViewCoverageGaps() {
  const highRiskGaps = coverageGaps.value.filter((gap) => gap.risk_level === 'high' || gap.risk_level === 'critical');
  emit('viewCoverageGaps', highRiskGaps);
}
</script>

<style scoped>
.coverage-details-card {
  height: 100%;
}

.coverage-container {
  position: relative;
}

.coverage-container::-webkit-scrollbar {
  width: 6px;
}

.coverage-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.coverage-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.coverage-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 动画效果 */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
